<template>
    <div class="internships-filters">
        <b-button
            class="btn-after-chevron-up d-sm-none btn-internships-filter-toggle"
            v-b-toggle.internships-filter
            block
            variant="primary">
            {{ $t('table.filter') }}
        </b-button>

        <b-collapse
            class="internships-filter-collapse"
            id="internships-filter"
            visible>
            <design-2021-internships-checkbox-filter-component
                v-model="selectedTags"
                v-if="internshipTags && internshipTags.length > 1"
                :title="$t('internship.tags')"
                filter-key="tags"
                :index="1"
                :options="internshipTags"
                @input="updateFilter">
            </design-2021-internships-checkbox-filter-component>

            <design-2021-internships-checkbox-filter-component
                v-model="selectedOrganizations"
                v-if="internshipOrganisations && internshipOrganisations.length > 1"
                :title="$t('common.organization')"
                filter-key="organization"
                :index="2"
                :options="internshipOrganisations"
                @input="updateFilter">
            </design-2021-internships-checkbox-filter-component>

            <design-2021-internships-checkbox-filter-component
                v-model="selectedLocations"
                v-if="internshipLocations && internshipLocations.length > 1"
                :title="$t('internship.locations')"
                filter-key="locations"
                :index="3"
                :options="internshipLocations"
                @input="updateFilter">
            </design-2021-internships-checkbox-filter-component>

            <div class="clear-internship-filter">
                <b-button
                    class="btn-clear-filters"
                    variant="primary"
                    @click="updateFilter([], 'reset')">
                    <i class="btn-eraser"></i>{{ $t('internship.reset_filters') }}
                </b-button>
            </div>
        </b-collapse>
    </div>
</template>

<script>
export default {
    name: 'design-2021-internships-filters-component',
    props: {
        internshipIn: {
            type: Array,
            required: true,
        },
        filtersIn: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            internshipTypes: [],
            filters: {},
            selectedTags: [],
            selectedOrganizations: [],
            selectedLocations: [],
        };
    },

    mounted() {
        Object.assign(this.filters, this.filtersIn);
    },

    methods: {
        updateFilter(checked, filterKey) {
            switch (filterKey) {
                case 'tags':
                    this.filters.tags = checked;
                    break;

                case 'locations':
                    this.filters.locations = checked;
                    break;

                case 'organization':
                    this.filters.organization = checked;
                    break;

                case 'type':
                    this.filters.type = checked;
                    break;
            }

            if (filterKey === 'reset') {
                this.selectedTags = [];
                this.selectedOrganizations = [];
                this.selectedLocations = [];
                this.filters.tags = [];
                this.filters.locations = [];
                this.filters.organization = [];
                this.filters.type = [];
            }

            this.$emit('filter-updated', this.filters);
        },
    },

    computed: {
        internshipTags() {
            if (this.internshipIn.length === 0) {
                return;
            }

            let tags = [];

            for (const internship of this.internshipIn) {
                for (const tag of internship.internship_tags) {
                    if (tags.length === 0) {
                        tags.push({
                            value: tag.id,
                            text: tag.name,
                        });
                    } else {
                        if (tags.map((label) => label.value).indexOf(tag.id) === -1) {
                            tags.push({
                                value: tag.id,
                                text: tag.name,
                            });
                        }
                    }
                }
            }

            return tags.sort((a, b) => {
                return a.text.localeCompare(b.text);
            });
        },

        internshipOrganisations() {
            if (this.internshipIn.length === 0) {
                return;
            }

            let organizations = [];

            for (const internship of this.internshipIn) {
                if (organizations.length === 0) {
                    organizations.push({
                        value: internship.internship_ownerable.id,
                        text: internship.internship_ownerable.name,
                    });
                } else if (
                    organizations
                        .map((organization) => organization.value)
                        .indexOf(internship.internship_ownerable_id) === -1
                ) {
                    organizations.push({
                        value: internship.internship_ownerable.id,
                        text: internship.internship_ownerable.name,
                    });
                }
            }

            return organizations.sort((a, b) => {
                return a.text.localeCompare(b.text);
            });
        },

        internshipLocations() {
            let locations = [];

            for (const internship of this.internshipIn) {
                if (locations.length === 0) {
                    locations.push({
                        value: internship.location,
                        text: internship.location,
                    });
                } else {
                    if (
                        locations
                            .map((location) => location.text.toLowerCase())
                            .indexOf(internship.location.toLowerCase()) === -1
                    ) {
                        locations.push({
                            value: internship.location,
                            text: internship.location,
                        });
                    }
                }
            }

            return locations.sort((a, b) => {
                return a.text.localeCompare(b.text);
            });
        },

        internshipTypesOptions() {
            if (this.internshipIn.length === 0) {
                return;
            }

            let internshipTypes = [];
            let internshipTypesIds = [];

            for (const internship of this.internshipIn) {
                for (const internshipType of internship.internship_tags) {
                    internshipTypesIds.push(internshipType.id);
                }
            }

            internshipTypesIds = [...new Set(internshipTypesIds)];

            internshipTypes = internshipTypesIds.map((id) => {
                for (const internship of this.internshipIn) {
                    for (const internshipType of internship.internship_tags) {
                        if (internshipType.id === id) {
                            return {
                                id: internshipType.id,
                                text: internshipType.name,
                                value: internshipType.id,
                            };
                        }
                    }
                }
            });

            return internshipTypes;
        },
    },
};
</script>

<style scoped lang="scss">
.internships-filters {
    margin-bottom: $grid-gutter-width;

    .btn-internships-filter-toggle {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');

        &:after {
            transition: transform 0.4s ease;
        }

        &.not-collapsed {
            &:after {
                transform: rotate(180deg);
            }
        }
    }
}

.clear-internship-filter {
    display: flex;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .btn-clear-filters {
        display: flex;
        flex: 1;
        flex-direction: row;
        line-height: 1;
        justify-content: center;

        i {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .internships-filters {
        max-width: 300px;
    }

    .clear-internship-filter {
        .btn-clear-filters {
            flex: 0 0 auto;
        }
    }
}
</style>
